import { useQuery } from '@tanstack/react-query';
import { Navigate, useSearchParams } from 'react-router-dom';
import { LeadSplitView, type LeadSplitApi } from '../../../../components/speedToLead/LeadSplitView';
import { CenteredSpinner } from '../../../../components/ui/Spinner';
import { firebaseAuth } from '../../../../lib/firebase';
import { leadsRedirectTarget, PORTAL_LEADS_PATH } from '../../../../lib/kyckConnect/portalLinks';
import { operationalAccounts } from '../../../../lib/portalAssets/pageState';
import { fetchPortalMe } from '../portalApi';
import { PortalShell } from '../PortalShell';
import { fetchPortalLeadIdForConversation, fetchPortalLeadSplit, fetchPortalLeadSplitDetail, fetchPortalRecordingUrl, setPortalLeadNeedsPerson, setPortalLeadOutcome, fetchPortalLeadCallOptions, startPortalLeadCall, sendPortalLeadText, setPortalLeadSamPaused } from '../speedToLead/portalApi';

// THE CLUB'S LEADS (KyckConnect, Phase 2 Part B): the shared LeadSplitView over the PORTAL api, in the Kyck Design System look.
// ⚠️ The look is the scoped `data-kc-theme="kyck"` wrapper below and nothing else: the staff app never wears it. The api file
// is the portal's own (/api/portal/speed-to-lead), the server scopes every request to the viewer's clubs, and no request here
// carries a phone number. `showTestToggle` and `approvalsHref` are not passed: test leads are staff only.

const API: LeadSplitApi = {
  list: (filters, cursor, accountId) => fetchPortalLeadSplit(accountId ?? null, filters, cursor),
  detail: (id, cursor) => fetchPortalLeadSplitDetail(id, cursor),
  setOutcome: (id, code) => setPortalLeadOutcome(id, code).then(() => undefined),
  setNeedsPerson: (id, on) => setPortalLeadNeedsPerson(id, on).then(() => undefined),
  callOptions: (id) => fetchPortalLeadCallOptions(id),
  startCall: (id, teamMemberId) => startPortalLeadCall(id, teamMemberId).then(() => undefined),
  sendText: (id, text) => sendPortalLeadText(id, text),
  setSamPaused: (id, paused) => setPortalLeadSamPaused(id, paused),
  loadPlayUrl: async (kind, id) => (await fetchPortalRecordingUrl(kind, id)).url,
};

export function PortalLeadsPage() {
  // The viewer's own clubs, from /me (the same list the Assets page uses). Several = the Club filter appears; the server still scopes, and a club outside it is a 404.
  const me = useQuery({ queryKey: ['portal-me'], queryFn: fetchPortalMe, retry: false });
  const clubs = me.data ? operationalAccounts(me.data.accounts) : undefined;
  return (
    <PortalShell signedInAs={firebaseAuth.currentUser?.email ?? null} nav wide fullWidthWhenFolded>
      <div data-kc-theme="kyck" className="rounded-[18px]" data-testid="portal-leads-theme">
        <div className="kc-root p-4">
          <div>
            <LeadSplitView api={API} queryKey={['portal-stl-leads-split']} clubs={clubs} />
          </div>
        </div>
      </div>
    </PortalShell>
  );
}

/** The old "Lead conversations" address. A link carrying `?c=<conversation>` opens that conversation's lead; anything else lands on the list. */
export function LeadConversationsRedirect() {
  const [params] = useSearchParams();
  const conversation = params.get('c');
  const resolved = useQuery({
    queryKey: ['portal-lead-for-conversation', conversation],
    queryFn: () => fetchPortalLeadIdForConversation(conversation as string),
    enabled: Boolean(conversation),
    retry: false,
  });
  if (conversation && resolved.isLoading) return <CenteredSpinner />;
  // A conversation outside the viewer's clubs (404) lands on the list, never on someone else's lead.
  return <Navigate to={leadsRedirectTarget(conversation, resolved.data?.leadId ?? null)} replace />;
}
